// reportModel — turn parsed Xero rows into the standardised report.
//
// Pure and React-free (see xeroParse.js for why).
//
// Three jobs:
//   1. map each account row onto a report line, honouring user overrides
//   2. evaluate the derived lines (Net Sales, Gross Profit, margins, …)
//   3. RECONCILE the computed Net Profit against Xero's own Net Profit row
//
// (3) is the reason this module can be trusted. Any mapping system will
// happily produce a confident, wrong number if an account silently falls
// through. Tying our bottom line back to Xero's makes that failure loud:
// if the delta isn't zero, something was missed or double-counted, and the
// UI says so instead of quietly shipping a wrong report.

// NOTE: these two relative imports carry explicit `.js` extensions, unlike the
// rest of the app (Vite resolves extensionless paths, so `../lib/awsdb` works
// elsewhere). Plain node ESM does NOT, and app/scripts/verify-eom.mjs runs this
// module under node to assert the figures. Keep the extensions.
import {
  REPORT_LINES,
  INPUT_LINE_KEYS,
  LINE_BY_KEY,
  classifySection,
  resolveAccount,
} from './accountMap.js';
import { findTotalRow } from './xeroParse.js';

// Xero's own bottom line, under any of the names it uses.
const NET_PROFIT_PATTERNS = [
  /^net profit/i,
  /^net (loss|income|surplus)/i,
  /^profit for the (period|month|year)/i,
  /^net profit\s*\/\s*\(loss\)/i,
];

// Cent-level tolerance. Xero rounds each displayed figure to 2dp, so summing
// displayed values can legitimately drift a fraction of a cent per account.
// Half a cent per account is generous enough to absorb that and tight enough
// to catch a genuinely missed account.
const RECONCILE_TOLERANCE = 0.01;

/**
 * Build the report.
 *
 * @param {object}  parsed     result of parseXeroWorkbook()
 * @param {string}  periodKey  which period column to report on
 * @param {object}  overrides  { [rowIndex]: lineKey | '__exclude__' } from the
 *                             review UI. Always wins over the rule engine.
 * @returns {{ meta, periodKey, lines, contributions, review, reconciliation }}
 */
export function buildReport(parsed, periodKey, overrides = {}) {
  if (!parsed) throw new Error('buildReport: no parsed workbook');
  const period = periodKey || parsed.periods?.[0]?.key;
  if (!period) throw new Error('buildReport: no period column to report on');

  // ── 1. map accounts → lines ──────────────────────────────────────────
  const contributions = {};              // lineKey → [{ rowIndex, label, value, confidence, source }]
  INPUT_LINE_KEYS.forEach(k => { contributions[k] = []; });
  const unmapped = [];                   // rows we could not place at all
  const excluded = [];                   // rows a user explicitly excluded

  for (const row of parsed.rows) {
    // Only real account rows carry figures we sum. Section headers have no
    // values, and Total rows are Xero's own subtotals — adding those would
    // double-count everything beneath them.
    if (row.kind !== 'account') continue;

    const raw = row.values?.[period];
    if (raw == null) continue;

    const sectionKind = classifySection(row.section);
    const override = overrides[row.rowIndex];

    let lineKey = null;
    let confidence = 'high';
    let source = 'rule';

    if (override === EXCLUDE) {
      excluded.push({ rowIndex: row.rowIndex, label: row.label, value: raw, section: row.section });
      continue;
    } else if (override && LINE_BY_KEY[override]) {
      lineKey = override;
      source = 'override';
    } else {
      const hit = resolveAccount(row.label, sectionKind);
      if (hit) { lineKey = hit.line; confidence = hit.confidence; }
    }

    if (!lineKey || !contributions[lineKey]) {
      unmapped.push({
        rowIndex: row.rowIndex,
        label: row.label,
        value: raw,
        section: row.section,
        sectionKind,
        suggested: null,
      });
      continue;
    }

    // Normalise polarity. Xero exports expenses and discounts with
    // inconsistent signs depending on account setup — sometimes positive
    // (a cost of 500), sometimes negative (-500 as a contra-revenue). The
    // report always PRESENTS these as positive magnitudes and the formulas
    // in accountMap subtract them explicitly, so take the magnitude here.
    // Doing it in one place keeps every downstream formula readable.
    const line = LINE_BY_KEY[lineKey];
    const value = line.sign === -1 ? Math.abs(raw) : raw;

    contributions[lineKey].push({
      rowIndex: row.rowIndex,
      label: row.label,
      value,
      rawValue: raw,
      confidence,
      source,
      section: row.section,
    });
  }

  // ── 2. resolve lines, inputs then derived, in declaration order ───────
  const metrics = {};
  const lines = [];

  for (const def of REPORT_LINES) {
    if (def.kind === 'header') {
      lines.push({ key: def.key, label: def.label, kind: 'header' });
      continue;
    }

    let value;
    if (def.kind === 'input') {
      const rows = contributions[def.key] || [];
      // null (not 0) when nothing mapped here — "no data" must stay
      // distinguishable from "genuinely zero" in the UI and the export.
      value = rows.length ? round2(rows.reduce((s, c) => s + c.value, 0)) : null;
    } else {
      // Derived. `metrics` holds every line resolved above this one.
      const raw = def.formula(metrics);
      value = raw == null ? null : (def.numFmt === 'pct' ? raw : round2(raw));
    }

    metrics[def.key] = value;
    lines.push({
      key: def.key,
      label: def.label,
      kind: def.kind,
      numFmt: def.numFmt,
      emphasis: !!def.emphasis,
      value,
      contributors: def.kind === 'input' ? (contributions[def.key] || []) : [],
    });
  }

  // Advertising is a called-out subset of opex, so a naive sum would count
  // those accounts twice. The rule engine already routes ad accounts to
  // `advertising` and never to `opex`, so no adjustment is needed here —
  // this assertion documents that invariant and fails loudly if a future
  // rule edit breaks it.
  const adRows = new Set((contributions.advertising || []).map(c => c.rowIndex));
  const doubleCounted = (contributions.opex || []).filter(c => adRows.has(c.rowIndex));

  // ── 3. reconcile against Xero's own bottom line ──────────────────────
  const xeroNetRow = findTotalRow(parsed.rows, NET_PROFIT_PATTERNS);
  const xeroNet = xeroNetRow ? xeroNetRow.values?.[period] ?? null : null;
  const computedNet = metrics.net_profit;

  let reconciliation;
  if (xeroNet == null || computedNet == null) {
    reconciliation = {
      status: 'unavailable',
      xero: xeroNet,
      computed: computedNet,
      delta: null,
      // Not a failure — some exports omit the Net Profit row entirely.
      // Say so plainly rather than implying the report was checked.
      message: xeroNetRow
        ? 'Xero\'s Net Profit row has no figure for this period, so the report couldn\'t be cross-checked.'
        : 'No Net Profit row found in the export, so the report couldn\'t be cross-checked against Xero.',
    };
  } else {
    const delta = round2(computedNet - xeroNet);
    const ties = Math.abs(delta) <= RECONCILE_TOLERANCE;
    reconciliation = {
      status: ties ? 'ok' : 'variance',
      xero: round2(xeroNet),
      computed: computedNet,
      delta,
      // Every figure in this sentence is currency-formatted — an unformatted
      // "95000" in a variance warning undermines the thing it's warning about.
      // The advice stays generic on purpose: there isn't always a review list
      // to point at (the detail can simply disagree with Xero's own total), so
      // don't promise the user a list that may not be on screen.
      message: ties
        ? 'Net Profit ties to Xero.'
        : `This report puts Net Profit at ${fmtMoney(computedNet, { decimals: 2 })} against Xero's ${fmtMoney(round2(xeroNet), { decimals: 2 })} — a difference of ${fmtMoney(Math.abs(delta), { decimals: 2 })}. Usually an account is unmapped or counted twice. Check the account mapping before sending this out.`,
    };
  }

  // Everything a human should eyeball before trusting the report.
  const lowConfidence = [];
  for (const key of INPUT_LINE_KEYS) {
    for (const c of contributions[key]) {
      if (c.confidence === 'low' && c.source !== 'override') {
        lowConfidence.push({ ...c, assignedTo: key });
      }
    }
  }

  return {
    meta: parsed.meta,
    periodKey: period,
    periods: parsed.periods,
    lines,
    metrics,
    contributions,
    review: {
      unmapped,
      lowConfidence,
      excluded,
      doubleCounted,
      // The review step can be skipped entirely when there is nothing to
      // look at and the numbers tie.
      needsAttention: unmapped.length > 0 || lowConfidence.length > 0 ||
        doubleCounted.length > 0 || reconciliation.status === 'variance',
    },
    reconciliation,
  };
}

// Sentinel for "user explicitly dropped this account from the report".
export const EXCLUDE = '__exclude__';

// Round to cents. Sums of 2dp figures otherwise accumulate float dust
// (0.1 + 0.2 = 0.30000000000000004) which then shows up in the
// reconciliation delta and looks like a real variance.
function round2(n) {
  if (n == null || !Number.isFinite(n)) return null;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ── formatting helpers, shared by the UI and the Excel export ──────────

export function fmtMoney(n, { decimals = 0 } = {}) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD',
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(n);
}

export function fmtPct(n) {
  if (n == null) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

export function fmtLineValue(line) {
  if (line.kind === 'header') return '';
  return line.numFmt === 'pct' ? fmtPct(line.value) : fmtMoney(line.value);
}
